import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "react-bootstrap";
import ButtonTriangleSpinner from "../ButtonTriangleSpinner/ButtonTriangleSpinner.jsx";
import { NotificationIcon } from "../../../assets/Svg/SvgImages.jsx";
import moment from "moment";
import { ATS } from "../../../features/ats/atsSlice";
import { SOCKET } from "../../../constants/constants";
import { parseJSON } from "../../../helpers/commonHelper";
import { socket } from "../../../App";
import { toastMsg } from "../Toast/Toast";
import "./NotificationList.scss";

/** Normalize backend read flags (boolean or 0/1). */
function isNotificationUnread(item) {
  if (item == null) return false;
  const raw = item.read ?? item.is_read ?? item.isRead ?? item.Read;
  if (typeof raw === "boolean") return !raw;
  if (typeof raw === "number") return raw === 0;
  if (typeof raw === "string") {
    const s = raw.trim().toLowerCase();
    if (s === "0" || s === "false") return true;
    if (s === "1" || s === "true") return false;
  }
  return raw === undefined || raw === null;
}
function notificationRowKey(item, index) {
  const id = item?.id ?? item?.notification_id;
  return id != null ? String(id) : `row-${index}`;
}
function resolveNotificationPayload(response) {
  const rows = response?.data ?? response;
  return {
    notifications: rows?.notifications ?? [],
    unreadCount: rows?.unreadCount ?? rows?.unread_count ?? 0
  };
}
const MENU_WIDTH = 420;
const MENU_GAP = 10;
const NotificationList = () => {
  const [show, setShow] = useState(false);
  const [menuPos, setMenuPos] = useState({
    top: 0,
    left: 0
  });
  const [loading, setLoading] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const [isLoadMore, setIsloadMore] = useState(true);
  const [paginationBody, setPaginationBody] = useState({
    offset: 0,
    limit: 10
  });
  const [notificationList, setNotificationList] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const toggleRef = useRef(null);
  const panelRef = useRef(null);
  const closeMenu = useCallback(() => setShow(false), []);
  const updateMenuPosition = useCallback(() => {
    const toggle = toggleRef.current;
    if (!toggle) return;
    const rect = toggle.getBoundingClientRect();
    const width = Math.min(MENU_WIDTH, window.innerWidth - 16);
    let left = rect.right - width;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    const top = rect.bottom + MENU_GAP;
    setMenuPos({
      top,
      left
    });
  }, []);
  const applyNotificationsResponse = useCallback((response, append) => {
    const {
      notifications: next,
      unreadCount
    } = resolveNotificationPayload(response);
    if (append) {
      setNotificationList(prev => [...prev, ...next]);
    } else {
      setNotificationList(next);
    }
    setUnreadNotificationCount(unreadCount);
    return next;
  }, []);
  const fetchNotificationsPage = useCallback(async (offset, limit, append) => {
    try {
      const response = await ATS.getNotifications({
        offset,
        limit
      });
      return applyNotificationsResponse(response, append);
    } catch {
      if (!append) setIsloadMore(false);
      return [];
    }
  }, [applyNotificationsResponse]);
  const refreshNotifications = useCallback(async () => {
    const limit = paginationBody.limit;
    setLoading(true);
    try {
      setPaginationBody({
        offset: 0,
        limit
      });
      const batch = await fetchNotificationsPage(0, limit, false);
      setIsloadMore(batch.length >= limit);
    } finally {
      setLoading(false);
    }
  }, [fetchNotificationsPage, paginationBody.limit]);
  const loadMore = async () => {
    try {
      const nextOffset = paginationBody.offset + 1;
      setPaginationBody({
        offset: nextOffset,
        limit: paginationBody.limit
      });
      setLoading(true);
      const result = await fetchNotificationsPage(nextOffset, paginationBody.limit, true);
      if (!result?.length) setIsloadMore(false);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    const limit = 10;
    setLoading(true);
    fetchNotificationsPage(0, limit, false).then(batch => {
      if (!cancelled) setIsloadMore(batch.length >= limit);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchNotificationsPage]);
  useEffect(() => {
    if (!socket) return undefined;
    socket.off(SOCKET.USER_NOTIFICATION);
    socket.on(SOCKET.USER_NOTIFICATION, order => {
      if (order.type !== "EVENT") toastMsg.info(order.message);
      const row = parseJSON(order);
      setNotificationList(prev => [row, ...prev]);
      setUnreadNotificationCount(c => Number.isFinite(Number(c)) ? Math.max(0, Math.floor(Number(c)) + 1) : 1);
    });
    return () => {
      socket.off(SOCKET.USER_NOTIFICATION);
    };
  }, []);
  useLayoutEffect(() => {
    if (!show) return undefined;
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [show, updateMenuPosition]);
  useEffect(() => {
    if (!show) return undefined;
    const onKeyDown = event => {
      if (event.key === "Escape") closeMenu();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [show, closeMenu]);
  const handleReadAll = async e => {
    e.preventDefault();
    e.stopPropagation();
    if (markingRead || unreadNotificationCount < 1) return;
    setMarkingRead(true);
    try {
      await ATS.markNotificationsRead({
        readAll: true
      });
      await refreshNotifications();
    } catch {
      /* toast handled in ATS */
    } finally {
      setMarkingRead(false);
    }
  };
  const handleNotificationClick = async (item, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (markingRead || !isNotificationUnread(item)) return;
    const id = item?.id ?? item?.notification_id;
    if (id == null || Number.isNaN(Number(id))) return;
    setMarkingRead(true);
    try {
      await ATS.markNotificationsRead({
        readAll: false,
        id: Number(id)
      });
      await refreshNotifications();
    } catch {
      /* toast handled in ATS */
    } finally {
      setMarkingRead(false);
    }
  };
  const unreadCount = Number.isFinite(Number(unreadNotificationCount)) ? Math.max(0, Math.floor(Number(unreadNotificationCount))) : 0;
  const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);
  const menu = show ? createPortal(<>
                <button type="button" aria-label="Close notifications" className="notification-dropdown-backdrop" onMouseDown={e => {
      e.preventDefault();
      e.stopPropagation();
      closeMenu();
    }} onClick={e => {
      e.preventDefault();
      e.stopPropagation();
      closeMenu();
    }} />
                <div ref={panelRef} className="notification-dropdown-panel" style={{
      top: menuPos.top,
      left: menuPos.left
    }} role="dialog" aria-label="Notifications">
                    <div className="notification-dropdown-toolbar">
                        <span className="notification-dropdown-toolbar-label">Notifications</span>
                        {unreadCount >= 1 ? <button type="button" className="notification-read-all-btn" disabled={markingRead} onClick={handleReadAll}>
                                {markingRead ? "Updating…" : "Read all"}
                            </button> : null}
                    </div>
                    <ul className="notification-dropdown-list">
                        {notificationList.length === 0 && !loading ? <li className="notification-item notification-item--empty">
                                No notifications yet
                            </li> : null}
                        {notificationList.map((item, i) => {
          const unread = isNotificationUnread(item);
          const rowClass = unread ? "notification-item notification-item--unread" : "notification-item notification-item--read";
          return <li key={notificationRowKey(item, i)} className={rowClass} role="button" tabIndex={0} onClick={e => handleNotificationClick(item, e)} onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              handleNotificationClick(item, e);
            }
          }}>
                                    <span className="notification-item__dot" aria-hidden />
                                    <div className="notification-item__body">
                                        <h5 className="notification-item__title">{item.title}</h5>
                                        <p className="notification-item__message">{item.message}</p>
                                        <time className="notification-item__date">
                                            {moment(item.created_at).format("DD/MM/YYYY")}{" "}
                                            <span>{moment(item.created_at).format("hh:mm A")}</span>
                                        </time>
                                    </div>
                                </li>;
        })}
                    </ul>
                    {notificationList.length >= paginationBody.limit && isLoadMore ? <div className="loadMorebtn">
                            <Button type="button" disabled={loading} onClick={loadMore} className="btn-style w-100">
                                {loading ? <span className="LoaderIcon">
                                        <ButtonTriangleSpinner size={18} />
                                    </span> : "Load More"}
                            </Button>
                        </div> : null}
                </div>
            </>, document.body) : null;
  return <div className="Notification_Dropdown">
            <button ref={toggleRef} type="button" className="notification-dropdown-toggle" aria-label="Notifications" aria-expanded={show} aria-haspopup="dialog" onClick={() => {
      setShow(prev => {
        const next = !prev;
        if (next) {
          // position on next paint via layout effect
          requestAnimationFrame(updateMenuPosition);
        }
        return next;
      });
    }}>
                <span className="notificationBellWrap">
                    <NotificationIcon />
                    {unreadCount >= 1 ? <span className="Show_Notification_No">{badgeLabel}</span> : null}
                </span>
            </button>
            {menu}
        </div>;
};
export default NotificationList;